import React, { useEffect, useState } from 'react';
import styles from './NGSListModal.module.css';
import { useParams, useNavigate } from 'react-router-dom'; // Added useNavigate

function NGSListModal({ isOpen, onClose, type }) {
    const { tmpKey } = useParams();
    const navigate = useNavigate(); // Added hook

    // Data grouping state
    const [groupedData, setGroupedData] = useState({
        patient: { normal: [], tumor: [] },
        organoid: { normal: [], tumor: [] }
    });
    
    // Count state
    const [counts, setCounts] = useState({
        patient: { normal: 0, tumor: 0 },
        organoid: { normal: 0, tumor: 0 }
    });
    
    const [isLoading, setIsLoading] = useState(false);
    const [gbcCode, setGbcCode] = useState('');

    useEffect(() => {
        if (!isOpen || !tmpKey) return;

        // 1. Parse GBCC Code
        const realKey = tmpKey.replaceAll('hashmark', '#');
        setGbcCode(realKey);

        const regex = /^GBC(\d{6})([TMN])#(\d+)$/;
        const match = realKey.match(regex);

        if (match) {
            const serial = match[1];      // Serial number (6 digits)
            const gbcType = match[2];     // T, N, M
            const seq = parseInt(match[3], 10); // Collection order

            // 2. Call backend API
            fetchNGSData(serial, gbcType, seq);
        } else {
            console.error("Invalid GBCC Code format");
            // Initialize
            resetData();
        }
    }, [isOpen, tmpKey, type]);

    const resetData = () => {
        const empty = { normal: [], tumor: [] };
        setGroupedData({ patient: empty, organoid: empty });
        setCounts({ patient: { normal: 0, tumor: 0 }, organoid: { normal: 0, tumor: 0 } });
    };

    // ★ [API Call] Send filtering conditions -> Backend returns filtered list
    const fetchNGSData = (serial, gbcType, seq) => {
        setIsLoading(true);

        const payload = {
            category: type, // 'WES' or 'RNA'
            serial: serial,
            gbcType: gbcType,
            seq: seq
        };

        fetch('/api/getLinkedNGSData', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        })
        .then(res => res.json())
        .then(data => {
            // Assume backend returns data meeting conditions (serial, seq, M status etc.)
            // data example: [{ ngsId: '...', sampleType: 'NT' }, { ngsId: '...', sampleType: 'TO' }]
            classifyData(data || []);
        })
        .catch(err => {
            console.error("Failed to fetch NGS data", err);
            resetData();
        })
        .finally(() => {
            setIsLoading(false);
        });
    };

    // ★ [Frontend Classification Logic] Place received data into 4 quadrants
    const classifyData = (list) => {
        const groups = {
            patient: { normal: [], tumor: [] },
            organoid: { normal: [], tumor: [] }
        };

        list.forEach(item => {
            const { sampleType } = item; // ngsId, sampleType required

            // 1. Patient Normal (NT, NB, NP)
            if (['NT', 'NB', 'NP'].includes(sampleType)) {
                groups.patient.normal.push(item);
            } 
            // 2. Patient Tumor (TT, TF)
            else if (['TT', 'TF'].includes(sampleType)) {
                groups.patient.tumor.push(item);
            } 
            // 3. Organoid Normal (NO)
            else if (sampleType === 'NO') {
                groups.organoid.normal.push(item);
            } 
            // 4. Organoid Tumor (TO)
            else if (sampleType === 'TO') {
                groups.organoid.tumor.push(item);
            }
        });

        setGroupedData(groups);
        setCounts({
            patient: {
                normal: groups.patient.normal.length,
                tumor: groups.patient.tumor.length
            },
            organoid: {
                normal: groups.organoid.normal.length,
                tumor: groups.organoid.tumor.length
            }
        });
    };

    // Updated handleSampleClick to navigate
    const handleSampleClick = (ngsId) => {
        navigate(`/NGS/${type}/${ngsId}`);
    };

    // Table renderer (UI unchanged)
    const MiniTable = ({ data, title, count }) => (
        <div className={styles.columnBox}>
            <div className={`${styles.colHeader} ${title === 'Normal' ? styles.headerNormal : styles.headerTumor}`}>
                {title} <span className={styles.countBadge}>({count})</span>
            </div>
            <div className={styles.tableWrapper}>
                <table className={styles.miniTable}>
                    <tbody>
                        {data.length > 0 ? (
                            data.map((row, idx) => (
                                <tr key={idx} onClick={() => handleSampleClick(row.ngsId)} className={styles.clickableRow}>
                                    <td>{row.ngsId}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td className={styles.noData}>{""}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );

    if (!isOpen) return null;

    return (
        <div className={styles.overlay} onClick={onClose}>
            {/* ... (rest of JSX remains exactly the same) ... */}
             <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                <div className={styles.header}>
                    <h3 className={styles.title}>
                        {type} List <span className={styles.subTitle}>({gbcCode})</span>
                    </h3>
                    <button className={styles.closeButton} onClick={onClose}>&times;</button>
                </div>

                <div className={styles.body}>
                    {isLoading ? (
                        <div className={styles.loading}>Loading...</div>
                    ) : (
                        <div className={styles.layoutContainer}>
                            {/* Patient Section */}
                            <div className={styles.sectionRow}>
                                <div className={styles.sectionLabel}>Patient Origin</div>
                                <div className={styles.gridTwoCols}>
                                    <MiniTable 
                                        data={groupedData.patient.normal} 
                                        title="Normal" 
                                        count={counts.patient.normal} 
                                    />
                                    <MiniTable 
                                        data={groupedData.patient.tumor} 
                                        title="Tumor" 
                                        count={counts.patient.tumor} 
                                    />
                                </div>
                            </div>

                            <div className={styles.divider}></div>

                            {/* Organoid Section */}
                            <div className={styles.sectionRow}>
                                <div className={styles.sectionLabel}>Organoid Origin</div>
                                <div className={styles.gridTwoCols}>
                                    <MiniTable 
                                        data={groupedData.organoid.normal} 
                                        title="Normal" 
                                        count={counts.organoid.normal} 
                                    />
                                    <MiniTable 
                                        data={groupedData.organoid.tumor} 
                                        title="Tumor" 
                                        count={counts.organoid.tumor} 
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                
                <div className={styles.footer}>
                    <button className={styles.footerCloseBtn} onClick={onClose}>Close</button>
                </div>
            </div>
        </div>
    );
}

export default NGSListModal;